import React, { useMemo, useState } from 'react';
import { Box, InputBase, Popover } from '@mui/material';
import { ArrowDropDown, Check, Search } from '@mui/icons-material';
import { SCHED } from './scheduleTheme';

export type ClassFilterValue = 'all' | 'booked' | { classKey: string };

export interface ClassFilterOption {
  key: string;
  name: string;
  color: string;
  count: number;
}

interface ScheduleClassFilterProps {
  value: ClassFilterValue;
  onChange: (value: ClassFilterValue) => void;
  options: ClassFilterOption[];
  allCount: number;
  bookedCount: number;
  /** "week" or "month" — the period the counts cover. */
  periodWord: string;
}

const dotSx = (background: string) => ({ width: 12, height: 12, borderRadius: '50%', background, flexShrink: 0 });
const ALL_DOT = `conic-gradient(${SCHED.primary}, #7B5CD6, #1F8A70, ${SCHED.primary})`;

const ScheduleClassFilter: React.FC<ScheduleClassFilterProps> = ({
  value,
  onChange,
  options,
  allCount,
  bookedCount,
  periodWord,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');

  const selectedClass = typeof value === 'object' ? options.find((o) => o.key === value.classKey) : undefined;
  const label = value === 'all' ? 'All classes' : value === 'booked' ? 'Booked sessions only' : selectedClass?.name || 'Class';
  const buttonDot = value === 'all' ? ALL_DOT : value === 'booked' ? SCHED.ink : selectedClass?.color || SCHED.muted;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const pick = (next: ClassFilterValue) => {
    onChange(next);
    setAnchorEl(null);
    setQuery('');
  };

  const row = (key: string, name: string, count: number, dot: string, selected: boolean, next: ClassFilterValue) => (
    <Box
      key={key}
      component="button"
      type="button"
      onClick={() => pick(next)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        width: '100%',
        p: '10px 14px',
        border: 0,
        borderRadius: '12px',
        backgroundColor: selected ? SCHED.todayTint : 'transparent',
        color: SCHED.ink,
        fontFamily: SCHED.font,
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': { backgroundColor: selected ? SCHED.todayTint : SCHED.hover },
        '&:focus-visible': { outline: `2px solid ${SCHED.ink}` },
      }}
    >
      <Box sx={dotSx(dot)} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: '15px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Box>
        <Box sx={{ fontSize: '13px', color: SCHED.muted }}>{count} this {periodWord}</Box>
      </Box>
      {selected && <Check sx={{ fontSize: 18, color: SCHED.primary }} />}
    </Box>
  );

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          height: 44,
          maxWidth: 260,
          pl: '14px',
          pr: '8px',
          borderRadius: '12px',
          border: `1px solid ${anchorEl ? SCHED.primary : SCHED.control}`,
          backgroundColor: '#FFFFFF',
          color: SCHED.ink,
          fontFamily: SCHED.font,
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          '&:hover': { backgroundColor: SCHED.hover },
          '&:focus-visible': { outline: `2px solid ${SCHED.ink}` },
        }}
      >
        <Box sx={dotSx(buttonDot)} />
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
        <ArrowDropDown sx={{ fontSize: 20, color: SCHED.muted }} />
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setQuery('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: '6px', width: 340, maxWidth: 'calc(100vw - 32px)', borderRadius: '18px', p: '12px', fontFamily: SCHED.font, boxShadow: '0 16px 40px rgba(40,20,26,0.16)' } }}
      >
        <Box sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', color: SCHED.muted, p: '4px 8px 10px' }}>
          SHOW SESSIONS FOR
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 44, px: '12px', mb: '8px', border: `1px solid ${SCHED.control}`, borderRadius: '12px' }}>
          <Search sx={{ fontSize: 20, color: SCHED.muted }} />
          <InputBase
            autoFocus
            fullWidth
            placeholder="Search class types"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ fontFamily: SCHED.font, fontSize: '14px' }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: 360, overflowY: 'auto' }}>
          {row('all', 'All classes', allCount, SCHED.ink, value === 'all', 'all')}
          {row('booked', 'Booked sessions only', bookedCount, SCHED.primary, value === 'booked', 'booked')}
          {matches.map((o) =>
            row(o.key, o.name, o.count, o.color, typeof value === 'object' && value.classKey === o.key, { classKey: o.key }),
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ScheduleClassFilter;
